import React, { useState, useEffect } from 'react'
import type { Theme } from '@material-ui/core'
import { makeStyles, Popover } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import { SketchPicker } from 'react-color'
import type { InputProps as MuiInputProps } from '@material-ui/core/Input'
import classNames from 'classnames'
import type { StyleClasses } from '../../styles'
import { InputField } from '../../'
import type { CoreColorPickerProps } from './index'

const StyledSketchPicker = SketchPicker as React.ComponentType<
  React.ComponentProps<typeof SketchPicker> & { styles?: any }
>

const SELECTION_STYLE_ID = 'ps-inline-color-picker-hide-selection'

const useStyles = makeStyles<Theme, InlineColorPickerProps>(
  (theme) => ({
    root: {},
    input: {},
    popover: {
      position: 'absolute',
      zIndex: 2
    },
    swatch: {
      height: 28,
      width: 28,
      '&:hover': {
        cursor: 'pointer'
      }
    },
    inlineSwatch: {
      width: 28,
      height: 28,
      cursor: 'pointer',
      borderWidth: 1,
      borderStyle: 'solid'
    },
    popoverPaper: {
      marginTop: theme.typography.pxToRem(8),
      borderRadius: theme.typography.pxToRem(12),
      overflow: 'hidden',
      border: `1px solid ${theme.palette.divider}`,
      background: theme.palette.common.white,
      boxShadow: theme.shadows[2]
    },
    popoverContent: {
      display: 'flex',
      flexDirection: 'column',
      padding: 0,
      touchAction: 'none',
      background: theme.palette.common.white,
      '& .sketch-picker': {
        borderRadius: theme.typography.pxToRem(12),
        overflow: 'hidden',
        border: 'none'
      },
      '& .sketch-picker .flexbox-fix input': {
        borderRadius: theme.typography.pxToRem(6),
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: 'none',
        background: theme.palette.common.white,
        padding: `${theme.typography.pxToRem(4)} ${theme.typography.pxToRem(12)}`,
        textAlign: 'center'
      },
      '& .sketch-picker .flexbox-fix .hue-horizontal > div > div': {
        width: `${theme.typography.pxToRem(3)} !important`,
        height: `${theme.typography.pxToRem(8)} !important`,
        borderRadius: `${theme.typography.pxToRem(1.5)} !important`,
        boxShadow: 'none !important',
        background: '#fff !important',
        transform: 'translateX(-3px) !important'
      },
      '& .sketch-picker .flexbox-fix .hue': {}
    },
    clearFooter: {
      borderTop: 'none',
      paddingLeft: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      paddingTop: theme.spacing(0.5),
      display: 'flex',
      justifyContent: 'flex-start',
      background: theme.palette.common.white
    },
    clearButton: {
      background: 'none',
      border: 'none',
      padding: 0,
      marginLeft: 0,
      color: theme.palette.text.primary,
      cursor: 'pointer',
      fontWeight: theme.typography.fontWeightRegular,
      letterSpacing: '0',
      textTransform: 'none',
      fontSize: theme.typography.pxToRem(12),
      textAlign: 'left',
      width: '100%'
    },
    clearButtonDisabled: {
      color: theme.palette.text.disabled,
      cursor: 'not-allowed'
    }
  }),
  { name: 'InlineColorPicker' }
)

export interface InlineColorPickerProps
  extends CoreColorPickerProps,
    Omit<MuiInputProps, 'onChange' | 'classes' | 'value' | 'label' | 'ref'> {
  swatchOnly?: boolean
  swatchIcon?: React.ReactNode
  labelAsValue?: boolean
  fullWidth?: boolean
  useResponsiveSwatchBorder?: boolean
  classes?: StyleClasses<typeof useStyles>
  onClose?: (hex?: string) => void
  presetColors?: string[]
  useClearOption?: boolean
  clearColor?: string
  defaultColor?: string
  onClear?: () => void
  hideSelectionHighlight?: boolean
}

function InlineColorPicker(props: InlineColorPickerProps) {
  const {
    onChange,
    value,
    swatchOnly = false,
    swatchIcon,
    labelAsValue,
    label,
    disabled,
    useResponsiveSwatchBorder,
    onClose,
    presetColors,
    useClearOption,
    clearColor,
    defaultColor,
    onClear,
    hideSelectionHighlight = false,
    ...otherProps
  } = props
  const theme = useTheme()
  const classes = useStyles(props)
  const sketchStyles = React.useMemo(
    () => ({
      default: {
        picker: {
          borderRadius: 0,
          boxShadow: 'none'
        },
        saturation: {
          position: 'relative'
        },
        Saturation: {
          radius: '12px',
          pointer: {
            width: 14,
            height: 14,
            borderRadius: '50%',
            transform: 'translate(-7px, -7px)',
            border: '1px solid #fff',
            boxShadow: '0 0 0 1px rgba(0,0,0,0.25)'
          },
          circle: {
            display: 'none'
          }
        },
        hue: {
          height: theme.typography.pxToRem(4),
          borderRadius: theme.typography.pxToRem(2)
        },
        Hue: {
          radius: theme.typography.pxToRem(2),
          slider: {
            width: theme.typography.pxToRem(18),
            height: theme.typography.pxToRem(6),
            borderRadius: theme.typography.pxToRem(1.5),
            background: '#fff',
            boxShadow: 'none',
            transform: 'translateX(calc(-9px))'
          }
        }
      }
    }),
    [theme]
  )
  const [color, setColor] = useState(value)
  const [anchorPosition, setAnchorPosition] = useState({ top: 0, left: 0 })
  const [open, setOpen] = useState(false)
  const [hasTypedHexChanges, setHasTypedHexChanges] = useState(false)

  useEffect(() => {
    setColor(value)
  }, [value])

  const handleClose = () => {
    if (hasTypedHexChanges && onChange && isCompleteHex(color) && color !== value) {
      onChange(color)
    }
    setHasTypedHexChanges(false)
    setOpen(false)
    if (onClose) {
      onClose(color)
    }
  }
  const normalizedDefaultColor = defaultColor ?? ''
  const currentColor = color ?? ''
  const canClear = useClearOption && Boolean(currentColor && currentColor !== normalizedDefaultColor)

  useEffect(() => {
    if (!hideSelectionHighlight || !open || typeof document === 'undefined') {
      return
    }
    const styleElement = document.createElement('style')
    styleElement.id = SELECTION_STYLE_ID
    styleElement.innerHTML = `
      .ProseMirror ::selection,
      .ProseMirror *::selection {
        background-color: transparent !important;
      }
    `
    document.head.appendChild(styleElement)
    return () => {
      styleElement.remove()
    }
  }, [hideSelectionHighlight, open])
  return (
    <div className={classes.root}>
      {swatchIcon && (
        <div
          onMouseDownCapture={(ev) => {
            ev.preventDefault()
          }}
          onMouseDown={(ev) => {
            ev.preventDefault()
          }}
          onClick={handleSwatchClick}
        >
          {swatchIcon}
        </div>
      )}
      {!swatchIcon && (
        <>
          {!swatchOnly && (
            <InputField
              {...(otherProps as any)}
              value={labelAsValue ? label : currentColor}
              disabled={disabled || labelAsValue ? true : false}
              label={labelAsValue ? '' : label}
              classes={{ root: classes.input, adornment: classes.inputAdornment }}
              onChange={handleInputChange}
              endAdornment={
                <Swatch
                  useResponsiveSwatchBorder={useResponsiveSwatchBorder}
                  className={classes.swatch}
                  hex={currentColor}
                  onClick={handleSwatchClick}
                />
              }
            />
          )}
          {swatchOnly && (
            <Swatch
              useResponsiveSwatchBorder={useResponsiveSwatchBorder}
              className={classes.swatch}
              hex={currentColor}
              onClick={handleSwatchClick}
            />
          )}
        </>
      )}

      <Popover
        open={open}
        anchorPosition={anchorPosition}
        anchorReference={'anchorPosition'}
        onClose={handleClose}
        PaperProps={{ className: classes.popoverPaper }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
      >
        <div className={classes.popoverContent} onMouseDown={handlePopoverMouseDown}>
          <StyledSketchPicker
            styles={sketchStyles}
            color={color}
            onChange={({ hex }) => {
              setColor(hex)
              if (isEditingPickerInput()) {
                if (!shouldPropagatePickerInputChange()) {
                  setHasTypedHexChanges(true)
                  return
                }
                setHasTypedHexChanges(false)
              }
              if (onChange && isCompleteHex(hex)) onChange(hex)
            }}
            disableAlpha={true}
            presetColors={presetColors}
          />
          {useClearOption && (
            <div className={classes.clearFooter}>
              <button
                type="button"
                className={classNames(classes.clearButton, {
                  [classes.clearButtonDisabled]: !canClear
                })}
                onClick={() => canClear && handleClear()}
                disabled={!canClear}
              >
                Clear
              </button>
            </div>
          )}
        </div>
      </Popover>
    </div>
  )

  function handleClear() {
    const fallback = clearColor ?? defaultColor ?? ''
    setColor(fallback)
    if (fallback && onChange) {
      onChange(fallback)
    }
    if (onClear) {
      onClear()
    }
  }

  function handleSwatchClick(ev: any) {
    const rect = ev.currentTarget.parentElement.parentElement.getBoundingClientRect()
    const { bottom, right } = rect
    if (!disabled) {
      setOpen(true)
      setAnchorPosition({ top: bottom, left: right })
    }
  }

  function handleInputChange(ev: React.ChangeEvent<HTMLInputElement>) {
    const hex = ev.target.value
    setColor(hex)
    setHasTypedHexChanges(true)
    if (onChange && isCompleteHex(hex)) {
      onChange(hex)
    }
  }

  function handlePopoverMouseDown(ev: React.MouseEvent<HTMLElement>) {
    const target = ev.target as HTMLElement
    if (target.closest('.sketch-picker, input, textarea, button, [contenteditable="true"]')) {
      return
    }
    ev.preventDefault()
  }

  function Swatch({ hex, useResponsiveSwatchBorder: _useResponsiveSwatchBorder, onClick }: any) {
    return (
      <div
        onMouseDownCapture={(ev) => {
          ev.preventDefault()
        }}
        onMouseDown={(ev) => {
          ev.preventDefault()
        }}
        onClick={onClick}
        className={classes.swatch}
        style={{ backgroundColor: hex ?? '#000', outline: '1px solid #7a7a7a' }}
      />
    )
  }

  function isCompleteHex(hex: string) {
    return /^#[0-9a-f]{6}$/i.test(hex.trim())
  }

  function isEditingPickerInput() {
    if (typeof document === 'undefined') {
      return false
    }
    const activeElement = document.activeElement as HTMLElement | null
    if (!activeElement) {
      return false
    }
    return Boolean(activeElement.closest('.sketch-picker') && activeElement.matches('input, textarea'))
  }

  function shouldPropagatePickerInputChange() {
    if (typeof document === 'undefined') {
      return true
    }
    const activeElement = document.activeElement as HTMLInputElement | HTMLTextAreaElement | null
    if (!activeElement) {
      return true
    }
    const rawValue = activeElement.value?.trim() ?? ''
    if (!rawValue) {
      return false
    }

    // RGB number fields should propagate immediately for live preview updates.
    if (/^-?\d+$/.test(rawValue)) {
      return true
    }

    // Hex field can be typed incrementally; only propagate once a full 6-digit value is entered.
    if (/^#?[0-9a-f]+$/i.test(rawValue)) {
      const normalizedHex = rawValue.startsWith('#') ? rawValue.slice(1) : rawValue
      return normalizedHex.length >= 6
    }
    return false
  }
}

export default InlineColorPicker
